import React, { Suspense, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AppProvider, useApp } from "@/AppContext";
import { API } from "@/AppContext";
import Navbar from "@/components/Navbar";
import ReviewNudgeBanner from "@/components/ReviewNudgeBanner";
import Footer from "@/components/Footer";
import AuthCallback from "@/pages/AuthCallback";

// Deferred, non-critical UI: interactive widgets + the homepage chooser load on
// demand so they don't block first paint / inflate the entry bundle.
const AssistantWidget = React.lazy(() => import("@/components/AssistantWidget"));
const FloatingAvailability = React.lazy(() => import("@/components/FloatingAvailability"));
const PathChooser = React.lazy(() => import("@/pages/PathChooser"));

// Route-based code splitting: every non-critical page loads on demand so the
// initial bundle (homepage LCP) stays small. PathChooser + AuthCallback stay eager.
const ForWorkers = React.lazy(() => import("@/pages/ForWorkers"));
const ForHirers = React.lazy(() => import("@/pages/ForHirers"));
const Auth = React.lazy(() => import("@/pages/Auth"));
const ForgotPassword = React.lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = React.lazy(() => import("@/pages/ResetPassword"));
const LaborerDashboard = React.lazy(() => import("@/pages/LaborerDashboard"));
const WorkerOnboarding = React.lazy(() => import("@/pages/WorkerOnboarding"));
const HirerSearch = React.lazy(() => import("@/pages/HirerSearch"));
const PaymentSuccess = React.lazy(() => import("@/pages/PaymentSuccess"));
const Inbox = React.lazy(() => import("@/pages/Inbox"));
const Thread = React.lazy(() => import("@/pages/Thread"));
const Admin = React.lazy(() => import("@/pages/Admin"));
const Pricing = React.lazy(() => import("@/pages/Pricing"));
const SubscriptionSuccess = React.lazy(() => import("@/pages/SubscriptionSuccess"));
const PostJob = React.lazy(() => import("@/pages/PostJob"));
const HirerWizard = React.lazy(() => import("@/pages/HirerWizard"));
const HirerVerify = React.lazy(() => import("@/pages/HirerVerify"));
const Jobs = React.lazy(() => import("@/pages/Jobs"));
const JobDetail = React.lazy(() => import("@/pages/JobDetail"));
const MyJobs = React.lazy(() => import("@/pages/MyJobs"));
const JobApplications = React.lazy(() => import("@/pages/JobApplications"));
const ManageJob = React.lazy(() => import("@/pages/ManageJob"));
const MyApplications = React.lazy(() => import("@/pages/MyApplications"));
const Referrals = React.lazy(() => import("@/pages/Referrals"));
const Account = React.lazy(() => import("@/pages/Account"));
const ExternalJobDetail = React.lazy(() => import("@/pages/ExternalJobDetail"));
const Press = React.lazy(() => import("@/pages/Press"));
const CityJobs = React.lazy(() => import("@/pages/CityJobs"));
const CityDirectory = React.lazy(() => import("@/pages/CityDirectory"));
const DeleteAccount = React.lazy(() => import("@/pages/DeleteAccount"));
const DataDeletion = React.lazy(() => import("@/pages/DataDeletion"));
const HireCity = React.lazy(() => import("@/pages/HireCity"));
const WorkerProfile = React.lazy(() => import("@/pages/WorkerProfile"));
const CompanyJobs = React.lazy(() => import("@/pages/CompanyJobs"));
const CompanySetup = React.lazy(() => import("@/pages/CompanySetup"));
const LegalPage = React.lazy(() => import("@/pages/LegalPage"));
const SmsTerms = React.lazy(() => import("@/pages/SmsTerms"));
const ReferAndEarn = React.lazy(() => import("@/pages/ReferAndEarn"));
const WorkerResources = React.lazy(() => import("@/pages/WorkerResources"));
const HirePromo = React.lazy(() => import("@/pages/HirePromo"));
const LosPlebes = React.lazy(() => import("@/pages/LosPlebes"));
const AffiliateDashboard = React.lazy(() => import("@/pages/AffiliateDashboard"));
const ChambaMatters = React.lazy(() => import("@/chamba-matters/ChambaMatters"));
const CMArticle = React.lazy(() => import("@/chamba-matters/CMArticle"));
const CMArchive = React.lazy(() => import("@/chamba-matters/CMArchive"));
const About = React.lazy(() => import("@/pages/About"));
const VsPage = React.lazy(() => import("@/pages/VsPage"));

const RouteFallback = () => (
  <div className="max-w-md mx-auto px-4 py-20 text-center font-bold uppercase tracking-wider text-zinc-400">Loading…</div>
);

// Forces Spanish UI for /es/* routes (reuses the same bilingual components + live data).
function ForceLang({ children, lang = "es" }) {
  const { lang: cur, setLang } = useApp();
  useEffect(() => { if (cur !== lang) setLang(lang); }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps
  return children;
}

function Protected({ children, role }) {
  const { user, loadingUser } = useApp();
  if (loadingUser) {
    return <div className="max-w-md mx-auto px-4 py-20 text-center font-bold uppercase tracking-wider">Loading…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) {
    return <Navigate to={user.role === "hirer" ? "/search" : "/dashboard"} replace />;
  }
  return children;
}

function DashboardRouter() {
  const { user, loadingUser } = useApp();
  if (loadingUser) return <div className="max-w-md mx-auto px-4 py-20 text-center font-bold uppercase tracking-wider">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Hirers get the Find Workers dashboard on its own; Refer & Earn lives on /referrals.
  if (user.role === "hirer") return <HirerSearch />;
  return (
    <>
      <LaborerDashboard />
      <div className="border-t-2 border-[#FF7A1A]/20 mt-4" data-testid="dashboard-referral-section">
        <Referrals />
      </div>
    </>
  );
}

function AppRouter() {
  const location = useLocation();
  const { authHeaders, lang, hideChrome } = useApp();

  // Scroll to top on every route change (footer link fix, etc.)
  React.useEffect(() => {
    // Skip when a URL hash (#section) is present — let native anchor handling win
    if (!location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
  }, [location.pathname, location.search, location.hash]);

  // Site-wide self-referencing canonical: every route emits a canonical (and
  // og:url) matching its own URL, so Google never treats sub-pages as the
  // homepage. Page-level usePageMeta sets the same value; this is the safety net.
  React.useEffect(() => {
    if (location.hash?.includes("session_id=")) return;
    const href = `https://chambanow.com${location.pathname}`;
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", href);
    const og = document.querySelector('meta[property="og:url"]');
    if (og) og.setAttribute("content", href);
  }, [location.pathname, location.hash]);

  // Lightweight first-party pageview tracker
  React.useEffect(() => {
    // Skip auth-callback flow so we don't log ?session_id=… into the DB
    if (location.hash?.includes("session_id=")) return;
    try {
      let sid = window.localStorage.getItem("cbn_sid");
      if (!sid) {
        sid = (crypto?.randomUUID?.() || (Date.now().toString(36) + Math.random().toString(36).slice(2)));
        window.localStorage.setItem("cbn_sid", sid);
      }
      // Fire-and-forget — never blocks nav, silently swallows errors
      axios.post(
        `${API}/analytics/pageview`,
        {
          path: location.pathname + (location.search || ""),
          referrer: document.referrer || null,
          session_id: sid,
          lang: lang || null,
        },
        { headers: authHeaders() },
      ).catch(() => {});
    } catch { /* ignore */ }
  }, [location.pathname, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Engaged-time beacon: measures tab-visible time per session so ≤5s bot/bounce
  // visits can be excluded from the visitor stats. Mounts once for the session.
  React.useEffect(() => {
    let sid;
    try {
      sid = window.localStorage.getItem("cbn_sid");
      if (!sid) {
        sid = (crypto?.randomUUID?.() || (Date.now().toString(36) + Math.random().toString(36).slice(2)));
        window.localStorage.setItem("cbn_sid", sid);
      }
    } catch { return; }
    let engaged = 0; // accumulated visible ms
    let last = (typeof document !== "undefined" && document.visibilityState === "visible") ? Date.now() : null;
    const flush = () => {
      if (last != null) { engaged += Date.now() - last; last = Date.now(); }
      try {
        const blob = new Blob([JSON.stringify({ session_id: sid, dwell_ms: Math.round(engaged) })], { type: "application/json" });
        navigator.sendBeacon?.(`${API}/analytics/dwell`, blob);
      } catch { /* ignore */ }
    };
    const onVis = () => {
      if (document.visibilityState === "visible") { last = Date.now(); }
      else { if (last != null) { engaged += Date.now() - last; last = null; } flush(); }
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", flush);
    const iv = setInterval(() => { if (document.visibilityState === "visible") flush(); }, 15000);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", flush);
      clearInterval(iv);
      flush();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // GA4: send a page_view on every SPA route change. Skip the very first render —
  // the initial landing view is sent from index.html — so the landing isn't double
  // counted. Suppressed for blocked countries (window.__ga4Blocked set in index.html).
  const gaFirst = React.useRef(true);
  useEffect(() => {
    if (gaFirst.current) { gaFirst.current = false; return; }
    if (window.__ga4Blocked) return;
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location.pathname, location.search]);

  // Handle OAuth callback BEFORE normal routing
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  // Chamba Matters uses its own layout (no main Navbar/Footer)
  const isChambaMatters = location.pathname.toLowerCase().startsWith("/chambamatters");
  if (isChambaMatters) {
    return (
      <div className="App flex flex-col min-h-screen bg-white">
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/chambamatters" element={<ChambaMatters />} />
            <Route path="/chambamatters/archive" element={<CMArchive />} />
            <Route path="/chambamatters/:slug" element={<CMArticle />} />
          </Routes>
        </Suspense>
      </div>
    );
  }

  // Onboarding wizard runs in a focused, distraction-free shell — no global navbar,
  // footer, floating assistant or review banner. The wizard renders its own minimal header.
  // /get-started renders inside the normal layout; the guided wizard hides the global
  // Navbar/Footer itself (via hideChrome) so the choice screen keeps the header/footer.

  return (
    <div className="App flex flex-col min-h-screen">
      <Navbar />
      {!hideChrome && <ReviewNudgeBanner />}
      <main className="flex-1">
        <Suspense fallback={<RouteFallback />}>
          <Routes>
          <Route path="/" element={<PathChooser />} />
          <Route path="/for-workers" element={<ForWorkers />} />
          <Route path="/for-hirers" element={<ForHirers />} />
          <Route path="/login" element={<Auth mode="login" />} />
          <Route path="/signup" element={<Auth mode="signup" />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={<DashboardRouter />} />
          <Route path="/welcome" element={<Protected role="laborer"><WorkerOnboarding /></Protected>} />
          <Route path="/search" element={<Protected role="hirer"><HirerSearch /></Protected>} />
          <Route path="/get-started" element={<Protected role="hirer"><HirerWizard /></Protected>} />
          <Route path="/verify" element={<Protected role="hirer"><HirerVerify /></Protected>} />
          <Route path="/payment-success" element={<Protected role="hirer"><PaymentSuccess /></Protected>} />
          <Route path="/inbox" element={<Protected><Inbox /></Protected>} />
          <Route path="/inbox/:threadId" element={<Protected><Thread /></Protected>} />
          <Route path="/admin" element={<Protected><Admin /></Protected>} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/about" element={<About />} />
          <Route path="/vs/:slug" element={<VsPage />} />
          <Route path="/subscription-success" element={<Protected role="hirer"><SubscriptionSuccess /></Protected>} />
          <Route path="/post-job" element={<Protected role="hirer"><PostJob /></Protected>} />
          <Route path="/post-job/:editId" element={<Protected role="hirer"><PostJob /></Protected>} />
          <Route path="/my-jobs" element={<Protected role="hirer"><MyJobs /></Protected>} />
          <Route path="/my-jobs/:jobId/applications" element={<Protected role="hirer"><JobApplications /></Protected>} />
          <Route path="/my-jobs/:jobId/manage" element={<Protected role="hirer"><ManageJob /></Protected>} />
          <Route path="/jobs" element={<Jobs />} />
          <Route path="/partner-jobs/:slug" element={<ExternalJobDetail />} />
          <Route path="/jobs/in/:citySlug" element={<CityJobs />} />
          <Route path="/cities" element={<CityDirectory />} />
          <Route path="/hire/:citySlug" element={<HireCity />} />
          <Route path="/hire/:citySlug/:skillSlug" element={<HireCity />} />
          {/* Spanish (es-US) routes — same live data + components, forced Spanish UI. */}
          <Route path="/es" element={<ForceLang><PathChooser /></ForceLang>} />
          <Route path="/es/precios" element={<ForceLang><Pricing /></ForceLang>} />
          <Route path="/es/contratar/:citySlug" element={<ForceLang><HireCity /></ForceLang>} />
          <Route path="/es/contratar/:citySlug/:skillSlug" element={<ForceLang><HireCity /></ForceLang>} />
          <Route path="/jobs/in/:citySlug/:skillSlug" element={<CityJobs />} />
          <Route path="/w/:shareId" element={<WorkerProfile />} />
          <Route path="/c/:slug" element={<CompanyJobs />} />
          <Route path="/company-setup" element={<CompanySetup />} />
          <Route path="/jobs/:jobId" element={<JobDetail />} />
          <Route path="/terms" element={<LegalPage doc="terms" />} />
          <Route path="/sms-terms" element={<SmsTerms />} />
          <Route path="/privacy" element={<LegalPage doc="privacy" />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/refer-and-earn" element={<ReferAndEarn />} />
          <Route path="/r" element={<ReferAndEarn />} />
          <Route path="/my-applications" element={<Protected role="laborer"><MyApplications /></Protected>} />
          <Route path="/referrals" element={<Protected><Referrals /></Protected>} />
          <Route path="/account" element={<Protected><Account /></Protected>} />
          <Route path="/press" element={<Press />} />
          <Route path="/press/:slug" element={<Press />} />
          <Route path="/worker-resources" element={<WorkerResources />} />
          <Route path="/free-month" element={<HirePromo />} />
          <Route path="/los-plebes" element={<LosPlebes />} />
          <Route path="/affiliate" element={<Protected><AffiliateDashboard /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </main>
      <Footer />
      <Suspense fallback={null}>
        {!hideChrome && <FloatingAvailability />}
        {!hideChrome && <AssistantWidget />}
      </Suspense>
    </div>
  );
}

export default function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <AppRouter />
      </BrowserRouter>
    </AppProvider>
  );
}
